import {
  getFormatter,
  InstrumentSubscriptionDataScope,
  isDefined,
  ListedInstrument,
  useInstrumentStoreItem,
} from '@eih-netbroker/common';
import { useMemo } from 'react';
import { ReactComponent as ErrorOutlineIcon } from '../../../assets/icons/alert_line.svg';
import { ReactComponent as ArrowDropDownIcon } from '../../../assets/icons/arrow-down-s-fill.svg';
import { ReactComponent as ArrowDropUpIcon } from '../../../assets/icons/arrow-up-s-fill.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { InstumentMarketPriceDisplay } from '../../../shared/components/instrument-market-price-display/InstrumentMarketPriceDisplay';
import { InstrumentSearchAutocomplete } from '../../../shared/components/instrument-search-autocomplete/InstrumentSearchAutocomplete';
import { NbTooltip } from '../../../shared/components/tooltip/Tooltip';
import { instrumentStore } from '../../../shared/data/instrumentStore';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { cssWhen } from '../../../shared/utils/themeUtils';
import classes from './WidgetInstrumentSearchAndDisplay.module.scss';

type WidgetInstrumentSearchAndDisplayWithoutPriceProps = {
  source?: undefined;
  instrument: ListedInstrument | null;
  updateInstrument: (instrument: ListedInstrument | null) => void;
  showPriceChanges?: false;
  disableSubscription: true;
};
type WidgetInstrumentSearchAndDisplayWithPriceProps = {
  source: string;
  instrument: ListedInstrument | null;
  updateInstrument: (instrument: ListedInstrument | null) => void;
  showPriceChanges?: boolean;
  disableSubscription?: false;
};

export type WidgetInstrumentSearchAndDisplayProps = {
  className?: string;
  warningTooltip?: string;
  disabled?: boolean;
  transactionId?: string;
  readOnly?: boolean;
  dataTestId?: string;
  dataScope?: Array<InstrumentSubscriptionDataScope>;
  displayPrice?: boolean;
} & (WidgetInstrumentSearchAndDisplayWithoutPriceProps | WidgetInstrumentSearchAndDisplayWithPriceProps);

export const WidgetInstrumentSearchAndDisplay = ({
  className,
  source,
  instrument,
  updateInstrument,
  showPriceChanges,
  disableSubscription,
  warningTooltip,
  disabled,
  transactionId,
  readOnly,
  dataTestId,
  dataScope,
  displayPrice,
}: WidgetInstrumentSearchAndDisplayProps) => {
  if (!source && !disableSubscription) {
    console.warn(`WidgetInstrumentSearchAndDisplay: source is not provided!`);
  }

  const { hasEStreamConnection, subscribeToInstrumentChanges } = useEStream();
  const { formatCurrency, formatNumberF2WithSign, formatPercentage } = getFormatter();
  const { translation } = useLocale();
  const instrumentStoreItem = useInstrumentStoreItem(instrument, instrumentStore, subscribeToInstrumentChanges, {
    source: source || 'WidgetInstrumentSearchAndDisplay-unknown-source',
    disableSubscription,
    dataScope,
  });

  const formattedPrice = useMemo(() => {
    if (instrument && instrumentStoreItem?.price?.value) {
      return formatCurrency(instrumentStoreItem.price.value, instrument);
    }
    return undefined;
  }, [instrument, instrumentStoreItem, formatCurrency]);

  const priceChange = useMemo(() => {
    if (instrument && instrumentStoreItem?.price?.value && instrumentStoreItem?.closePrice) {
      return instrumentStoreItem.price.value - instrumentStoreItem.closePrice;
    }
    return undefined;
  }, [instrument, instrumentStoreItem]);

  const priceChangePerc = useMemo(() => {
    if (instrument && instrumentStoreItem?.price?.value && instrumentStoreItem?.closePrice && isDefined(priceChange)) {
      return priceChange / instrumentStoreItem.closePrice;
    }
    return undefined;
  }, [instrument, instrumentStoreItem, priceChange]);

  return (
    <>
      <div className={cssWhen(classes.header, className)}>
        <InstrumentSearchAutocomplete
          isPopperWide
          disabled={disabled}
          onSelectInstrument={updateInstrument}
          selection={instrument ? instrument : null}
          className={classes.headerInstrumentSearch}
          wrapperClassName={classes.headerInstrumentSearchWrapper}
          transactionId={transactionId}
          readOnly={readOnly}
          dataTestId={dataTestId}
        />
        {instrument && !hasEStreamConnection && (
          <NbTooltip
            title={warningTooltip || translation.app.estream.unavailable}
            placement={'bottom'}
          >
            <span className={classes.errorIcon}>
              <ErrorOutlineIcon />
            </span>
          </NbTooltip>
        )}
        {hasEStreamConnection && displayPrice !== false && (
          <>
            {formattedPrice && isDefined(priceChange) && isDefined(priceChangePerc) && (
              <>
                <InstumentMarketPriceDisplay
                  instrument={instrument!}
                  withCurrency
                  price={instrumentStoreItem?.price}
                  className={classes.headerPriceText}
                />
                {showPriceChanges !== false && (
                  <div
                    className={cssWhen(classes.headerChangeText, [
                      priceChange < 0,
                      classes.headerChangeTextIsNegative,
                      classes.headerChangeTextIsPositive,
                    ])}
                  >
                    {priceChange < 0 ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                    <span>
                      {formatNumberF2WithSign(priceChange)} ({formatPercentage(priceChangePerc)})
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};
